// Copyright 2023 Sorama B.V.
import { customElement } from "aurelia-framework";
import { Chart, PointStyle } from "chart.js";
import { Point2D } from "datatypes/SoramaLocalApi";
import { RTData, RTDecayTime } from "datatypes/SpecialMeasurement";
import { ReportTemplate } from "../ReportTemplate";

@customElement("rt-report-template")
export class RtReportTemplate extends ReportTemplate<RTData> {
    private chartRef: HTMLCanvasElement;

    private rtTable: Point2D[] = [];
    private xAxisFreq = [
        80, 100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000,
    ];
    private xAxisFreqOctaveThree = [125, 250, 500, 1000, 2000, 4000];
    protected bandWidthString: string;

    private decayTimeString: string;
    protected roomVolume: string;
    private rtChartData: number[];

    public reportChanged() {
        if (!this.report) return;

        if (this.data.octaveMode == 1) {
            this.bandWidthString = "1/3 oct.";
        } else {
            this.bandWidthString = "1/1 oct.";
        }

        this.cleanUpUIView();

        this.roomVolume = this.data.volumeReceivingRoom;

        this.decayTimeString = RTDecayTime[this.data.decayTime]?.toString();
        this.rtChartData = this.data.rt60ReverberationTimePerOctaveBand;

        // To make it compatible with previous version
        if (!this.decayTimeString || !this.rtChartData) {
            this.decayTimeString = this.data.measuredTime;
        }

        if (this.data.octaveMode === 1) {
            this.rtChartData.forEach((yAxisreport: number, index) => {
                this.rtTable.push({ x: this.xAxisFreq[index], y: yAxisreport });
            });
        } else {
            this.rtChartData.forEach((yAxisreport: number, index) => {
                this.rtTable.push({ x: this.xAxisFreqOctaveThree[index], y: yAxisreport });
            });
        }
    }

    attached() {
        if (!this.report) return;

        const xLable = this.data.octaveMode === 1 ? this.xAxisFreq : this.xAxisFreqOctaveThree;
        const barThickness = this.data.octaveMode === 1 ? 15 : 40;
        const ticks =
            this.data.octaveMode === 1
                ? {
                      autoSkip: false,
                      maxRotation: 0,
                      minRotation: 0,
                      display: true,
                      beginAtZero: true,
                      callback: function (value, index, report) {
                          // index from xAxisFreq array
                          if (index == 1 || index == 4 || index == 7 || index == 10 || index == 13 || index == 16) {
                              return value;
                          }
                      },
                  }
                : {
                      autoSkip: false,
                      maxRotation: 0,
                      minRotation: 0,
                      display: true,
                      beginAtZero: true,
                  };

        this.drawRtChart(this.rtChartData, xLable, barThickness, ticks);
    }

    private cleanUpUIView(): void {
        this.roomVolume = undefined;
        this.rtTable.splice(0, this.rtTable.length);
    }

    protected drawRtChart(octavereport: number[], xLable, barThickness, ticks): void {
        const yLabel = "Reverberation Time T[s]";

        new Chart(this.chartRef, {
            type: "bar",
            data: {
                labels: xLable,
                datasets: [
                    {
                        barThickness: barThickness,
                        borderWidth: 1,
                        fill: false,
                        backgroundColor: "rgba(47,116,191,255)",
                        lineTension: 0,
                        borderColor: "black",
                        data: octavereport,
                        pointStyle: {} as PointStyle,
                        pointRadius: 0,
                    },
                ],
            },
            plugins: [
                {
                    beforeDatasetsDraw: function (chart, args, pluginOptions) {
                        const {
                            ctx,
                            chartArea: { top, bottom, left, right },
                        } = chart;

                        ctx.save();
                        ctx.beginPath();
                        ctx.lineWidth = 1;
                        ctx.strokeStyle = "rgba(40, 40, 40, 1)";
                        ctx.moveTo(left, top);
                        ctx.lineTo(right - 1, top);
                        ctx.lineTo(right - 1, bottom);
                        ctx.lineTo(left, bottom);
                        ctx.closePath();
                        ctx.stroke();
                    },
                },
            ],
            options: {
                animation: {
                    duration: 0,
                },
                aspectRatio: 1 / 1.33,
                legend: { display: false },
                maintainAspectRatio: true,
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                color: "black",
                                display: false,
                                drawBorder: true,
                            },
                            scaleLabel: {
                                display: true,
                                labelString: "Frequency [Hz]",
                            },
                            offset: true, // Start from 0
                            ticks: ticks,
                        },
                    ],
                    yAxes: [
                        {
                            gridLines: {
                                color: "black",
                                display: true,
                                drawBorder: true,
                                drawOnChartArea: false,
                            },
                            scaleLabel: {
                                display: true,
                                labelString: yLabel,
                            },
                            ticks: {
                                beginAtZero: true,
                            },
                        },
                    ],
                },
            },
        });
    }
}
